<template>
  <div
    class="fm-form-input"
    :class="{
      'mt-2': !!label,
      'cursor-no-drop': disabled,
      'border-red-600':
        highlightIfErrors && Array.isArray(errors) && errors.length > 0
    }"
  >
    <label v-if="label" for="name">
      {{ label }}
      <span v-if="required" class="text-red-500 capitalize">*</span>
    </label>
    <Combobox v-model="localValueObject" :disabled="disabled">
      <Float
        adaptive-width
        :offset="2"
        portal
        :auto-placement="{
          autoAlignment: true,
          allowedPlacements: ['bottom', 'top']
        }"
      >
        <div
          class="flex w-full flex-row overflow-hidden"
          @keydown.capture="onKeydown"
        >
          <ComboboxInput
            autocomplete="nope"
            autofill="nope"
            class="w-full border-none py-2 pr-10 pl-3 text-sm leading-5 focus:ring-0"
            :display-value="(val) => val?.[attributeLabel] ?? ''"
            :placeholder="placeholder"
            @change="query = $event.target.value"
            @focus="$emit('focus')"
          />
          <div class="absolute inset-y-0 right-3 flex items-center gap-2">
            <div v-if="requesting" class="flex items-center">
              <FontAwesomeIcon
                :icon="['fal', 'spinner-third']"
                class="animate-spin"
              />
            </div>
            <ComboboxButton
              ref="button"
              class="flex items-center"
              @click="onListOpening"
            >
              <FontAwesomeIcon :icon="['fal', 'chevron-down']" />
            </ComboboxButton>
            <button
              v-if="
                !required && (localValue || localValueObject?.[attributeLabel])
              "
              type="button"
              tabindex="-1"
              class="shrink-0 text-red-600"
              @click.prevent="clear"
            >
              <FontAwesomeIcon :icon="['far', 'times']"></FontAwesomeIcon>
            </button>
          </div>
        </div>
        <ComboboxOptions
          class="max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg focus:outline-none dark:border dark:border-slate-500 dark:bg-slate-800 dark:text-gray-300"
          :class="[widthClass]"
        >
          <template v-if="options === null">
            <div class="relative cursor-default px-4 py-2 select-none">
              {{ $t('global.taper_qq') }}
            </div>
          </template>
          <template v-else-if="options.length === 0">
            <div class="relative cursor-default px-4 py-2 select-none">
              {{ $t('global.no_result_found') }}
            </div>
          </template>
          <ComboboxOption
            v-for="option in options"
            v-else
            :key="option.id"
            v-slot="{ selected, active, disabled }"
            as="template"
            :value="option"
          >
            <li
              class="relative cursor-default py-2 pr-2 pl-8 select-none"
              :class="{
                'bg-gray-200 text-gray-700': disabled,
                'bg-primary-500 text-contrast-primary': selected,
                'bg-primary-300 text-white': active && revealActive,
                '': !active && !selected
              }"
              @pointerdown="explicitPick = $event.button === 0"
              @pointercancel="explicitPick = false"
              @pointermove="revealActive = true"
            >
              <slot
                name="option"
                :option="option"
                :active="active && revealActive"
                :selected="selected"
                :disabled="disabled"
              >
                <span
                  class="block truncate"
                  :class="{ 'font-medium': selected, 'font-normal': !selected }"
                >
                  {{ option[attributeLabel] }}
                </span>
              </slot>
              <span
                v-if="selected"
                class="absolute inset-y-0 left-0 flex items-center bg-primary-500 pl-3 text-contrast-primary"
              >
                <FontAwesomeIcon :icon="['fas', 'check']" />
              </span>
            </li>
          </ComboboxOption>
          <ComboboxOption
            v-if="allowTags && queryValue"
            v-slot="{ active }"
            :value="queryValue"
          >
            <li
              class="relative cursor-default py-2 pr-4 pl-8 select-none"
              :class="{
                'bg-primary-300 text-white': active,
                'text-gray-900': !active
              }"
            >
              <span class="block truncate font-normal italic">
                {{ queryValue[attributeLabel] }}
              </span>
            </li>
          </ComboboxOption>
        </ComboboxOptions>
      </Float>
    </Combobox>
  </div>
  <ErrorContainer :error-key="name" :errors="errors" />
</template>

<script>
import { defineComponent } from 'vue'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions
} from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import apiClient from '@u/apiClient'
import { debounce } from '@u/debounce'
import ErrorContainer from '@c/addf-package/components/BaseShowEditInput/ErrorContainer.vue'
import { Float } from '@headlessui-float/vue'
import { apiPath } from '@u/apiPath'

export default defineComponent({
  name: 'SelectAjax',
  components: {
    Float,
    ErrorContainer,
    ComboboxButton,
    FontAwesomeIcon,
    ComboboxOption,
    ComboboxOptions,
    ComboboxInput,
    Combobox
  },
  props: {
    value: {
      type: [String, Number],
      required: false,
      default: null
    },
    valueObject: {
      type: Object,
      required: true,
      default: () => {}
    },
    label: {
      type: String,
      required: false,
      default: null
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    url: {
      type: String,
      required: true
    },
    ajaxExtraParams: {
      type: Object,
      required: false,
      default: () => {}
    },
    trackBy: {
      type: String,
      required: false,
      default: 'id'
    },
    attributeLabel: {
      type: String,
      required: false,
      default: 'name'
    },
    name: {
      type: String,
      required: false,
      default: null
    },
    errors: {
      type: Object,
      required: false,
      default: () => {}
    },
    allowTags: {
      type: Boolean,
      required: false,
      default: false
    },
    regexTag: {
      type: String,
      required: false,
      default: '.*'
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    termsKey: {
      type: String,
      required: false,
      default: 'terms'
    },
    optionsWidth: {
      type: String,
      required: false,
      default: null
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    highlightIfErrors: {
      type: Boolean,
      required: false,
      default: false
    },
    // When set, opening the select with an empty query lists the results of
    // a request sent with these params (and ajaxExtraParams), without terms.
    initialParams: {
      type: Object,
      required: false,
      default: null
    },
    // Delay (ms) between the last keystroke and the search request. Shorten it
    // only for endpoints backed by a search index (Scout/Typesense).
    searchDebounce: {
      type: Number,
      required: false,
      default: 1000
    }
  },
  emits: [
    'update:value',
    'update:valueObject',
    'change',
    'clear',
    'open',
    'focus'
  ],
  data() {
    return {
      requesting: false,
      query: '',
      options: null,
      initialOptions: null,
      // Off while the initial list shows: Headless UI makes its first option
      // active on opening, before the user points at or reaches any. It is
      // only hidden, not deactivated: aria-activedescendant still points at
      // it (Headless UI 1.7 offers no way to open with no active option).
      revealActive: true
    }
  },
  computed: {
    localValue: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('update:value', value)
      }
    },
    localValueObject: {
      get() {
        return this.valueObject
      },
      set(value) {
        // Headless UI selects the active option on Tab or blur, and opening
        // the list makes the first option active: on the initial list
        // (nothing typed), only an explicit pick may change the value.
        const explicitPick = this.explicitPick
        this.explicitPick = false
        if (this.initialParams && !this.query && !explicitPick) {
          return
        }
        this.localValue = value[this.trackBy]
        this.$emit('update:valueObject', value)
        this.$emit('change', value)
      }
    },
    widthClass() {
      if (!this.optionsWidth) {
        return 'w-full'
      }
      switch (this.optionsWidth) {
        case 'large':
          return 'w-96'
        default:
          return 'w-84'
      }
    },
    queryValue() {
      if (this.allowTags && !!this.query && this.query.length) {
        let re = new RegExp(this.regexTag, 'g')
        if (this.query.match(re)) {
          return { [this.trackBy]: null, [this.attributeLabel]: this.query }
        }
      }
      return null
    }
  },
  watch: {
    query(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.debouncedSearch(newValue)
        if (newValue) {
          this.revealActive = true
          this.leaveInitialList()
        }
        if (!newValue) {
          this.loadInitialOptions()
        }
      }
    },
    initialParams: {
      handler() {
        this.resetInitialOptions()
        // Params known after the list was opened (e.g. a position granted
        // late).
        if (this.isOpen()) {
          this.loadInitialOptions()
        }
      },
      deep: true
    },
    ajaxExtraParams: {
      handler() {
        this.resetInitialOptions()
        if (this.isOpen()) {
          this.loadInitialOptions()
        }
      },
      deep: true
    },
    searchDebounce() {
      this.debouncedSearch.cancel()
      this.createDebouncedSearch()
    }
  },
  beforeUnmount() {
    this.abortController?.abort()
    this.debouncedSearch.cancel()
  },
  created() {
    this.initialRequest = null
    this.abortController = null
    this.explicitPick = false
    this.createDebouncedSearch()
  },
  methods: {
    remove(index) {
      this.localValues.splice(index, 1)
    },
    clear() {
      this.$emit('update:value', null)
      this.$emit('update:valueObject', null)
      this.$emit('clear')
    },
    createDebouncedSearch() {
      this.debouncedSearch = debounce((terms) => {
        if (terms && terms.length >= 2) {
          this.search(terms)
        }
      }, this.searchDebounce)
    },
    search(terms) {
      // Failures are already toasted by apiClient, cancellations are silent.
      this.fetchOptions({ [this.termsKey]: terms }).catch(() => {})
    },
    isOpen() {
      return this.$refs.button?.$el.getAttribute('aria-expanded') === 'true'
    },
    // Captured before Headless UI's own handler, which selects on Enter.
    onKeydown(event) {
      const isArrow = ['ArrowDown', 'ArrowUp'].includes(event.key)
      const isJump = ['Home', 'End', 'PageUp', 'PageDown'].includes(event.key)
      if (isArrow && !this.revealActive && this.isOpen()) {
        // The first option is already active, only hidden: the first arrow
        // reveals it instead of moving past it.
        event.preventDefault()
        event.stopPropagation()
        this.revealActive = true
        this.explicitPick = true
      } else if (isArrow && !this.isOpen()) {
        // Headless UI opens the list on an arrow: same as the chevron, once
        // its own handler has run (this one is captured before it, and the
        // browser runs microtasks between the two, so $nextTick is too early).
        setTimeout(() => this.onListOpening())
      } else if (isArrow || isJump) {
        this.revealActive = true
        this.explicitPick = true
      } else if (event.key === 'Enter') {
        // Enter on a list with nothing highlighted must not pick its hidden
        // first option.
        this.explicitPick = this.revealActive
      } else if (event.key === 'Escape') {
        this.explicitPick = false
      }
    },
    // Initial options come with the chevron or an arrow key only: a list
    // opened on focus would make a plain Tab through the form select its
    // first option.
    onListOpening() {
      this.explicitPick = false
      this.$nextTick(() => {
        if (this.isOpen()) {
          this.$emit('open')
          if (this.initialParams) {
            // The input shows the selected value, not the last search: reopen
            // on the initial list rather than on stale search results.
            this.query = ''
          }
          this.loadInitialOptions()
        }
      })
    },
    loadInitialOptions() {
      if (!this.initialParams || this.query) {
        return
      }
      // A fresh initial list: nothing highlighted, nothing picked yet (an
      // arrow pressed on an earlier list must not validate this one).
      this.revealActive = false
      this.explicitPick = false
      if (this.initialOptions) {
        // A search still running would replace the list once answered.
        this.abortController?.abort()
        this.options = this.initialOptions
        return
      }
      // The button click also focuses the input: one request for both.
      if (this.initialRequest) {
        return
      }
      const request = this.fetchOptions(this.initialParams)
        .then((options) => {
          this.initialOptions = options
        })
        .catch(() => {})
        .finally(() => {
          if (this.initialRequest === request) {
            this.initialRequest = null
          }
        })
      this.initialRequest = request
    },
    // Params changed: a cached or pending list belongs to the old ones.
    resetInitialOptions() {
      this.initialOptions = null
      this.abortInitialRequest()
    },
    abortInitialRequest() {
      if (this.initialRequest) {
        this.abortController?.abort()
        this.initialRequest = null
      }
    },
    // Typing leaves the initial list: until the search answers, show the
    // "type something" hint rather than nearby options Tab could pick.
    leaveInitialList() {
      this.abortInitialRequest()
      if (this.initialOptions && this.options === this.initialOptions) {
        this.options = null
      }
    },
    // A new request aborts the one in flight: its results no longer match
    // what the user typed.
    fetchOptions(params) {
      this.abortController?.abort()
      const controller = new AbortController()
      this.abortController = controller
      this.requesting = true
      return apiClient
        .get(apiPath(this.url), {
          params: { ...params, ...this.ajaxExtraParams },
          signal: controller.signal
        })
        .then((response) => {
          if (!controller.signal.aborted) {
            this.options = response.data
          }
          return response.data
        })
        .finally(() => {
          if (this.abortController === controller) {
            this.abortController = null
            this.requesting = false
          }
        })
    }
  }
})
</script>
